import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../AuthContext";
import toast from "react-hot-toast";
import RecruitGlanceLogo from "../components/RecruitGlanceLogo";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, setUser, loading: authLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [error, setError] = useState("");
  const [requiresPolicyAcceptance, setRequiresPolicyAcceptance] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard_recruiter");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("verified") === "true") {
      toast.success("Email verified! You can now login.");
    }
    if (params.get("session_expired") === "true") {
      setError("Your session expired. Please sign in again.");
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoadingLogin(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/login`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, accepted_terms: acceptedPolicies }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        const msg = data.detail || "Login failed";
        if (res.status === 428) {
          setRequiresPolicyAcceptance(true);
          setError("");
          return;
        }
        setError(msg);
        toast.error(msg);
        return;
      }

      localStorage.removeItem("token");
      setUser(data.user);

      toast.success("Login successful!");
      navigate("/dashboard_recruiter");
    } catch (err) {
      setError("Server error. Try again.");
      toast.error("Server error. Try again.");
    } finally {
      setLoadingLogin(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#03140f]">
        <p className="text-sm text-white/70">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#03140f] text-white">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <RecruitGlanceLogo dark />
        <Link
          to="/register"
          className="rounded-full border border-emerald-300/30 px-4 py-2 text-sm font-semibold text-emerald-50 transition hover:border-emerald-200 hover:bg-white/10"
        >
          Create account
        </Link>
      </header>

      <main className="mx-auto grid min-h-[calc(100vh-88px)] w-full max-w-6xl items-center gap-10 px-6 pb-16 pt-4 lg:grid-cols-[1fr_440px]">
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="max-w-2xl space-y-6"
        >
          <span className="inline-flex rounded-full border border-emerald-300/30 bg-emerald-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-100">
            Recruiter Workspace
          </span>
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
            See the right candidates at a glance.
          </h1>
          <p className="max-w-xl text-base leading-7 text-emerald-50/75">
            Sign in to review job pipelines, rank applicants, inspect skill gaps,
            and continue screening from your RecruitGlance dashboard.
          </p>
          <div className="grid gap-3 text-sm text-emerald-50/80 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="font-semibold text-white">AI ranking</p>
              <p className="mt-1 text-xs leading-5 text-emerald-50/60">
                Top matches with reasoning
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="font-semibold text-white">Skill evidence</p>
              <p className="mt-1 text-xs leading-5 text-emerald-50/60">
                Clear gaps and signals
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="font-semibold text-white">Recruiter flow</p>
              <p className="mt-1 text-xs leading-5 text-emerald-50/60">
                Jobs, candidates, rankings
              </p>
            </div>
          </div>
        </motion.section>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full rounded-3xl border border-white/10 bg-white p-6 text-slate-950 shadow-2xl shadow-black/30 sm:p-8"
        >
          <div className="mb-10 space-y-3 text-center">
            <h2 className="text-3xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-500">
              Sign in to continue to your hiring dashboard
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Work email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
            />

            <input
              type="password"
              placeholder="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
            />

            {requiresPolicyAcceptance && (
              <label className="flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-left text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={acceptedPolicies}
                  onChange={(e) => setAcceptedPolicies(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#00533d] focus:ring-[#00533d]"
                  required
                />
                <span>
                  I accept the{" "}
                  <Link to="/terms" className="font-semibold text-[#00533d] hover:underline">
                    Terms of Use
                  </Link>
                  ,{" "}
                  <Link to="/privacy" className="font-semibold text-[#00533d] hover:underline">
                    Privacy Policy
                  </Link>
                  , and AI disclaimer before using RecruitGlance with real job and CV data.
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={loadingLogin || (requiresPolicyAcceptance && !acceptedPolicies)}
              className="mt-2 w-full rounded-full bg-[#00533d] py-3 text-sm font-semibold text-white transition hover:bg-[#006f52] disabled:opacity-40"
            >
              {loadingLogin ? "Logging in..." : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/forgot_password")}
              className="text-sm font-medium text-slate-500 hover:text-[#00533d] hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <p className="mt-10 text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <Link to="/register" className="font-semibold text-[#00533d] hover:underline">
              Create one
            </Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
